// PolitiqueConfidentialite.jsx
// ———————————————————————————————————————————————————————————
// Page Politique de confidentialité (RGPD)
// Remplacer les [CHAMPS] puis router vers /politique-de-confidentialite
// ———————————————————————————————————————————————————————————

import React from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

function LegalLayout({ title, subtitle, children }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 5, px: 2 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "1200px",
          borderRadius: "20px",
          px: { xs: 3, md: 6 },
          pt: { xs: 4, md: 6 },
          pb: { xs: 5, md: 6 },
          backgroundColor: "#F8F8F8",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <Typography
          gutterBottom
          sx={{
            color: "#000",
            fontWeight: 900,
            fontSize: { xs: "2.2rem", md: "3rem" },
            lineHeight: 1.2,
            mb: 1.5,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ color: "#444", fontWeight: 500, fontSize: "1.1rem", mb: 3 }}>
            {subtitle}
          </Typography>
        )}
        <Divider sx={{ mb: 4 }} />
        {children}
      </Paper>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.35rem", md: "1.55rem" }, color: "#000", mt: 3, mb: 1 }}>
      {children}
    </Typography>
  );
}

function Paragraph({ children }) {
  return (
    <Typography sx={{ color: "#444", fontSize: "1.05rem", lineHeight: 1.7, mb: 2 }}>
      {children}
    </Typography>
  );
}

export default function PolitiqueConfidentialite() {
  return (
    <>
      <Navbar />
      <Box sx={{ height: "50px" }} />
        <LegalLayout
        title={<><span>Politique de </span><Box component="span" sx={{ color: "#449F2D" }}>confidentialité</Box></>}
        subtitle="Transparence sur les données collectées, les finalités, vos droits et nos engagements."
        >
        <Paragraph>Dernière mise à jour : [JJ mois AAAA]</Paragraph>

        <SectionTitle>Responsable du traitement</SectionTitle>
        <Paragraph>
            <strong>[Société]</strong> [forme] – [adresse] – RCS [ville] [SIREN]<br />
            Contact RGPD : <MuiLink href="mailto:[email RGPD]">[email RGPD]</MuiLink> {`[ / DPO : Nom, email ]`}
        </Paragraph>

        <SectionTitle>Données traitées</SectionTitle>
        <Paragraph>
            Identité/coordonnées ; informations sur les véhicules/équipements ; données de souscription et sinistres ;
            données de paiement (traitées par [prestataire]) ; messages via formulaires partenaires ; données techniques (IP, logs),
            navigation & cookies (voir Politique de cookies).
        </Paragraph>

        <SectionTitle>Finalités et bases légales</SectionTitle>
        <List dense sx={{ color: "#444" }}>
            <ListItem><ListItemText primary="Souscription et gestion des contrats/sinistres : exécution du contrat" /></ListItem>
            <ListItem><ListItemText primary="Relation client & partenaires, support, réclamations : contrat/intérêt légitime" /></ListItem>
            <ListItem><ListItemText primary="Lutte contre la fraude & sécurité : intérêt légitime et obligations légales" /></ListItem>
            <ListItem><ListItemText primary="Facturation, comptabilité & obligations légales : obligation légale" /></ListItem>
            <ListItem><ListItemText primary="Statistiques d’audience : consentement (si non exemptés)" /></ListItem>
            <ListItem><ListItemText primary="Prospection (email/SMS) : consentement ou intérêt légitime clients existants" /></ListItem>
        </List>

        <SectionTitle>Destinataires</SectionTitle>
        <Paragraph>
            Personnel habilité ; assureur(s) et partenaires gestion ; prestataires (hébergement, paiement, email/SMS,
            analytics, cartographie/géocodage) ; autorités en cas d’obligation légale.
        </Paragraph>

        <SectionTitle>Transferts hors UE</SectionTitle>
        <Paragraph>
            Le cas échéant, encadrés par des <em>clauses contractuelles types</em> ou mécanismes équivalents.
            Informations et copies des garanties disponibles sur demande.
        </Paragraph>

        <SectionTitle>Durées de conservation (principales)</SectionTitle>
        <List dense sx={{ color: "#444" }}>
            <ListItem><ListItemText primary="Prospects : 3 ans après le dernier contact" /></ListItem>
            <ListItem><ListItemText primary="Clients/contrats : durée du contrat + 5 ans (sauf obligations plus longues)" /></ListItem>
            <ListItem><ListItemText primary="Dossiers sinistres : durée du dossier + jusqu’à 10 ans" /></ListItem>
            <ListItem><ListItemText primary="Logs techniques : jusqu’à 12 mois" /></ListItem>
            <ListItem><ListItemText primary="Cookies : voir Politique de cookies" /></ListItem>
        </List>

        <SectionTitle>Vos droits</SectionTitle>
        <Paragraph>
            Accès, rectification, effacement, limitation, portabilité, opposition, directives post-mortem.
            Retrait du consentement à tout moment pour les traitements concernés. Exercice des droits :
            <MuiLink href="mailto:[email RGPD]"> [email RGPD]</MuiLink> / [adresse postale].<br />
            En cas de difficulté non résolue, vous pouvez saisir la CNIL : <MuiLink href="https://www.cnil.fr" target="_blank" rel="noreferrer">cnil.fr</MuiLink>.
        </Paragraph>

        <SectionTitle>Sécurité</SectionTitle>
        <Paragraph>
            Mesures techniques et organisationnelles adaptées : chiffrement en transit, contrôle d’accès,
            journalisation, cloisonnement, revue des prestataires, tests réguliers.
        </Paragraph>

        <SectionTitle>Mineurs</SectionTitle>
        <Paragraph>
            Le Site n’est pas destiné aux mineurs sans autorisation du titulaire de l’autorité parentale.
        </Paragraph>

        <SectionTitle>Modifications</SectionTitle>
        <Paragraph>
            Nous pouvons mettre à jour la présente politique ; la date de mise à jour figure en tête.
            En cas de changement substantiel, une information sera fournie par les canaux appropriés.
        </Paragraph>

        <Divider sx={{ my: 3 }} />
        <Paragraph>
            Voir aussi la <MuiLink href="/politique-de-cookies">Politique de cookies</MuiLink>.
        </Paragraph>
        </LegalLayout>
        <Footer/>
    </>
  );
}